import { useCallback, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { userContext } from '@utils/hyperdx';
import { checkAndRefreshCacheIfNeeded, getUserCache, setUserCache } from './userCacheService';
import {
	checkUserCompletedRegistration,
	createUser,
	CreateUserPayload,
	deleteUser,
	deleteUserFile,
	fetchUsers,
	getUserByConnectionCode,
	getUserByCropsAndLanguages,
	getUserById,
	updateUser,
	UpdateUserPayload,
} from './usersService';
import { User, useUsersStore } from './usersStore';

export function useUser() {
	const { user, setUser, setLoading } = useUsersStore();

	useEffect(() => {
		const cachedUser = getUserCache();
		if (cachedUser) {
			setUser(cachedUser);
			setLoading(false);
			
			// Set user context in HyperDX for cached user
			userContext.setUser(
				cachedUser.id,
				cachedUser.email,
				cachedUser.first_name,
				cachedUser.last_name,
				{
					role: cachedUser.role,
					isCompany: cachedUser.is_company,
					country: cachedUser.country,
					connectionCode: cachedUser.connection_code,
				}
			);
		}
	}, [setUser, setLoading]);

	useEffect(() => {
		const checkCache = async () => {
			await checkAndRefreshCacheIfNeeded();
		};

		checkCache();

		const intervalId = setInterval(checkCache, 5 * 60 * 1000);

		return () => clearInterval(intervalId);
	}, []);

	const { data, error, isLoading } = useSWR('users', fetchUsers, {
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		revalidateOnMount: true,
		dedupingInterval: 0,

		onSuccess: (user) => {
			setUser(user as User);
			setLoading(false);

			setUserCache(user as User);

			const storageData = localStorage.getItem('user');

			if (!storageData) {
				localStorage.setItem('user', JSON.stringify(user));
			}

			// Set user context in HyperDX with the required format: '<firstname lastname>-<user-id>'
			const userData = user as User;
			userContext.setUser(
				userData.id,
				userData.email,
				userData.first_name,
				userData.last_name,
				{
					role: userData.role,
					isCompany: userData.is_company,
					country: userData.country,
					connectionCode: userData.connection_code,
				}
			);
		},
		onError: () => {
			setLoading(false);
			const cachedUser = getUserCache();
			if (cachedUser) {
				setUser(cachedUser);
			}
		},
		onLoadingSlow: () => {
			setLoading(true);
		},
		loadingTimeout: 3000,
	});

	useEffect(() => {
		if (isLoading) {
			setLoading(true);
		}
	}, [isLoading, setLoading]);

	if (!data && user) {
		return { data: user, error: null, isLoading: false };
	}

	return {
		data,
		error,
		isLoading,
	};
}

export function useGetUserByConnectionCode() {
	const { data, error, isLoading, mutate } = useSWR('userByConnectionCode', null, {
		revalidateOnFocus: true,
		revalidateOnMount: true,
		revalidateOnReconnect: true,
	});
	const getUserByCode = async (connectionCode: string) => {
		const result = await getUserByConnectionCode(connectionCode);

		await mutate(result, false);

		return result;
	};

	return {
		data,
		error,
		isLoading,
		getUserByCode,
	};
}
export function useGetUserById() {
	const { data, error, isLoading, mutate } = useSWR('userById', null, {
		revalidateOnFocus: true,
		revalidateOnMount: true,
		revalidateOnReconnect: true,
		dedupingInterval: 0,
	});
	const { setUserByIds } = useUsersStore();

	const getUserByIds = async (ids: string[]) => {
		const result = await getUserById(ids);

		setUserByIds(result);
		await mutate(result, false);

		return result;
	};

	return {
		data,
		error,
		isLoading,
		getUserByIds,
	};
}

export function useGetUserByCropsAndLanguages(
	crop_id?: string,
	languages?: string[],
	monitoring: boolean = false
) {
	const shouldFetch = !!crop_id && !!languages?.length;
	const key = shouldFetch ? ['specialists', crop_id, languages.join(',')] : null;

	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			if (!crop_id || !languages?.length) return null;
			const result = await getUserByCropsAndLanguages(crop_id, languages, monitoring);

			return result;
		},
		{
			revalidateOnFocus: true,
			revalidateOnReconnect: true,
			revalidateOnMount: true,
			dedupingInterval: 5000,
		}
	);

	return {
		specialists: data,
		error,
		isLoading,
	};
}

export function useCheckUserCompletedRegistration() {
	const { data, error, isLoading } = useSWR('authCheck', checkUserCompletedRegistration, {
		revalidateOnFocus: true,
		revalidateOnReconnect: true,
		revalidateOnMount: true,
		dedupingInterval: 0,
	});

	const checkStatus = useCallback(async () => await mutate('authCheck'), []);

	return {
		data,
		error,
		isLoading,
		checkStatus,
	};
}

export function useCreateUser() {
	return async (data: CreateUserPayload) => {
		await createUser(data);
		mutate('users');
	};
}

export function useUpdateUser() {
	return async (data: UpdateUserPayload) => {
		await updateUser(data);
		mutate('users');
	};
}

export function useDeleteUserFile() {
	return async (fileId: string) => {
		await deleteUserFile(fileId);
		mutate('users');
	};
}

export function useDeleteUser() {
	return async () => {
		await deleteUser();
		mutate('users');
	};
}
